import { useEffect, useState, Suspense, useMemo } from "react";
import {
  Box,
  Button,
  CssBaseline,
  LinearProgress,
  Typography,
  Avatar
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import MainMenu from "./MainMenu";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { useNavigate, Outlet } from "react-router-dom";
import theme from "../theme/vs-theme";
import { useAuth } from "../hooks/useAuth";
import { useRole } from "../hooks/useRole";
import { useBreadcrumb } from "../hooks/useBreadcrumb";
import { ChatNotificationsContext } from "../hooks/useChatNotifications";
import { ChatModalContext } from "../hooks/useChatModal";
import { useChatPusher } from "../hooks/useChatPusher";
import { useApi } from "../hooks/useApi";
import { useTranslation } from "react-i18next";
import { hasPathologyFeature } from "../constants/pathologyConfigs";
import ChatModal from "./ChatModal";
import withMessages from "./WithMessages";

const Layout = ({ showMessage }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = useRole();
  const { firstTitles, secondTitle, goBack } = useBreadcrumb();
  const { get } = useApi();
  const { onMessageSent } = useChatPusher();
  const [unseenMessages, setUnseenMessages] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showSurveys, setShowSurveys] = useState(true);
  const [chatModalPatientId, setChatModalPatientId] = useState(null);
  const [chatModalPatientInfo, setChatModalPatientInfo] = useState(null);

  useEffect(() => {
    const manageWindowSizeChange = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", manageWindowSizeChange);
    return () => {
      window.removeEventListener("resize", manageWindowSizeChange);
    };
  }, []);

  useEffect(() => {
    const pathologiesArray = user?.user?.pathologies
      ? Object.values(user.user.pathologies)
      : [];
    const hasPathologyWithSurveys = pathologiesArray.some(p =>
      hasPathologyFeature(p.id, 'surveys')
    );
    setShowSurveys(hasPathologyWithSurveys);
  }, [user]);

  const defaultWidth = windowWidth - 350 + "px";

  const getContacts = () => {
    if (!user?.user?.chat_user_token) return;
    get("/chat/v1/chatify/api/getContacts", {
      useChatToken: true,
      onSuccess: (response) => {
        setUnseenMessages(response.data?.unseenCount);
      },
    });
  };

  useEffect(() => {
    if (!role.includes("doctor") || !user?.user?.chat_user_token) return;
    return onMessageSent(() => {
      if (!window.location.pathname.startsWith("/chat")) {
        getContacts();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, role]);

  const chatNotificationsValue = useMemo(
    () => ({ refreshUnseenCount: getContacts }),
    [user]
  );

  const chatModalValue = useMemo(
    () => ({
      openChat: (patientId, patientInfo) => {
        setChatModalPatientInfo(patientInfo ?? null);
        setChatModalPatientId(patientId);
      },
    }),
    []
  );

  const styles = {
    title: {
      padding: "12.5px",
      fontSize: "19px",
      fontWeight: 500,
      fontFamily: "Poppins",
      marginLeft: "3px",
      lineHeight: "150%",
      maxWidth: "250px",
      whiteSpace: "nowrap",
    },
  };

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ width: "100vw", height: "100vh", borderRadius: "2px", p: 4 }}>
        <div style={{ display: "flex" }}>
          <div>
            <MainMenu
              unseenMessages={unseenMessages}
              role={role}
              showSurveys={showSurveys}
            />
          </div>
          <div
            style={{
              marginLeft: "20px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ display: "flex" }}>
              <div>
                {goBack ? (
                  <div>
                    <Button
                      startIcon={<img src={"/back.png"} alt={"Avatar"} />}
                      variant="contained"
                      sx={{ color: "blue", minWidth: "100px" }}
                      color="secondary"
                      component="span"
                      onClick={() => navigate(goBack)}
                    >
                      {t("goback")}
                    </Button>
                  </div>
                ) : null}
              </div>

              <div style={{ marginLeft: "15px", display: "flex" }}>
                {firstTitles.map((firstTitle, index) => (
                  <Box
                    key={index}
                    sx={[
                      styles.title,
                      {
                        background: "#FCFCFC",
                        color: "#3988E3",
                        borderRadius: index == 0 ? "15px 0px 0px 15px" : "0px",
                      },
                    ]}
                  >
                    <Typography>{t(firstTitle)}</Typography>
                  </Box>
                ))}
                <Box
                  sx={[
                    styles.title,
                    {
                      background: "#3657D9",
                      color: "#FCFCFC",
                      borderRadius:
                        firstTitles.length == 0
                          ? "15px"
                          : "0px 15px 15px 0px",
                    },
                  ]}
                >
                  <Typography sx={{overflow: "hidden", textOverflow: "ellipsis"}}>{secondTitle}</Typography>
                </Box>
              </div>
            </div>
            <Grid
              sx={{ marginTop: "20px", maxWidth: "100%" }}
              container
              spacing={0}
            >
              <div
                style={{
                  width: defaultWidth,
                  maxWidth: defaultWidth,
                  overflowX: "auto",
                }}
              >
                <Suspense fallback={<LinearProgress />}>
                  <ChatNotificationsContext.Provider value={chatNotificationsValue}>
                    <ChatModalContext.Provider value={chatModalValue}>
                      {role.length > 0 ? <Outlet /> : <></>}
                      <ChatModal
                        patientId={chatModalPatientId}
                        onClose={() => setChatModalPatientId(null)}
                        showMessage={showMessage}
                      />
                    </ChatModalContext.Provider>
                  </ChatNotificationsContext.Provider>
                </Suspense>
              </div>
            </Grid>
          </div>
          <div
            style={{
              position: "absolute",
              display: "flex",
              top: "30px",
              right: "30px",
              minWidth: "150px",
              maxWidth: "400px",
            }}
          >
            <Button
              sx={{
                borderRadius: "15px !important",
                p: 1,
                right: "-20px",
                zIndex: 10,
                color: "#737589",
                nowrap: true,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              startIcon={<Avatar src={user?.user?.avatar?.url} />}
              color="secondary"
              variant="contained"
              onClick={() => navigate("/profile")}
            >
              {user?.user?.name}
            </Button>
            <Button
              sx={{
                width: 100,
                fontSize: 9,
                bg: "#3988E3",
                p: 1,
                lineHeight: 1,
              }}
              variant="contained"
              onClick={() => logout()}
            >
              <div spacing={0} sx={{ p: 0, m: 0, ml: 1 }}>
                <div xs={12}>
                  <img src={"/log-out.png"} alt={"Avatar"} />
                </div>
                <div xs={12}>{t("logout")}</div>
              </div>
            </Button>
          </div>
        </div>
      </Box>
    </MuiThemeProvider>
  );
};

export default withMessages(Layout);
